import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail } from "lucide-react";

const HELP_LINKS = [
  { label: "Contact", href: "mailto:hello@aliver.co.uk" },
  { label: "Shipping Information", href: "/legal/shipping" },
  { label: "Returns & Refunds", href: "/legal/returns" },
  { label: "FAQ", href: "#faq" },
];
const LEGAL_LINKS = [
  { label: "Privacy Policy", href: "/legal/privacy" },
  { label: "Terms & Conditions", href: "/legal/terms" },
  { label: "Subscription Terms", href: "/legal/subscription-terms" },
];

const toId = (l) => l.toLowerCase().replace(/[^a-z]+/g, "-");

const FooterLink = ({ label, href }) =>
  href.startsWith("/legal") ? (
    <Link
      to={href}
      data-testid={`footer-link-${toId(label)}`}
      className="text-sm text-[#5C5C5C] hover:text-ink transition-colors duration-300"
    >
      {label}
    </Link>
  ) : (
    <a
      href={href}
      data-testid={`footer-link-${toId(label)}`}
      className="text-sm text-[#5C5C5C] hover:text-ink transition-colors duration-300"
    >
      {label}
    </a>
  );

export const Footer = () => (
  <footer className="border-t border-champagne/70 bg-cream" data-testid="footer">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 md:py-20 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_auto]">
      <div>
        <div className="flex items-center gap-2.5">
          <span className="font-display text-2xl tracking-[0.18em] text-gold-deep">ALIVER</span>
        </div>
        <p className="mt-5 max-w-xs text-sm text-[#5C5C5C] leading-relaxed">
          24K gold hydrogel eye masks for a simple, calming at-home skincare ritual.
        </p>
        <a
          href="mailto:hello@aliver.co.uk"
          data-testid="footer-contact-email"
          className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-gold-deep transition-colors duration-300"
        >
          <Mail size={15} strokeWidth={1.5} /> hello@aliver.co.uk
        </a>
      </div>
      <div>
        <h4 className="text-xs font-semibold tracking-[0.24em] uppercase text-[#8C8C8C]">Help</h4>
        <ul className="mt-5 space-y-3">
          {HELP_LINKS.map((l) => (
            <li key={l.label}>
              <FooterLink label={l.label} href={l.href} />
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="text-xs font-semibold tracking-[0.24em] uppercase text-[#8C8C8C]">Legal</h4>
        <ul className="mt-5 space-y-3">
          {LEGAL_LINKS.map((l) => (
            <li key={l.label}>
              <FooterLink label={l.label} href={l.href} />
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="text-xs font-semibold tracking-[0.24em] uppercase text-[#8C8C8C]">Follow</h4>
        <div className="mt-5 flex gap-3">
          {[
            { icon: Instagram, label: "Instagram" },
            { icon: Facebook, label: "Facebook" },
            { icon: Youtube, label: "YouTube" },
          ].map((s) => (
            <a
              key={s.label}
              href="#"
              aria-label={s.label}
              data-testid={`footer-social-${s.label.toLowerCase()}`}
              className="w-10 h-10 rounded-full border border-champagne bg-white flex items-center justify-center text-[#5C5C5C] hover:text-gold-deep hover:border-gold-light transition-colors duration-300"
            >
              <s.icon size={17} strokeWidth={1.5} />
            </a>
          ))}
        </div>
      </div>
    </div>
    <div className="border-t border-champagne/70">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col md:flex-row gap-3 items-center justify-between text-xs text-[#8C8C8C]">
        <span data-testid="footer-copyright">© {new Date().getFullYear()} ALIVER. All rights reserved.</span>
        <span>Individual experiences may vary. Cosmetic product — not intended to treat any condition.</span>
      </div>
    </div>
  </footer>
);
